/**
 * NetworkManager — 網路通訊管理器
 *
 * 負責與星靈王伺服器的所有通訊：
 * - REST API（認證、配對）
 * - WebSocket（對戰即時通訊）
 * - Token 管理（localStorage 存取、自動刷新）
 * - 自動重連機制
 * - 心跳 Ping/Pong
 */

// ============================================================
// 類型定義（對應伺服器 Go 結構體）
// ============================================================

/** 伺服器 URL，可由環境變數 VITE_SERVER_URL 配置 */
import { getLogger } from '../utils/logger';

const log = getLogger('NetworkManager');

const SERVER_URL = import.meta.env.VITE_SERVER_URL as string || (
  typeof window !== 'undefined' && window.location.hostname !== 'localhost'
    ? `${window.location.protocol}//${window.location.host}`
    : 'http://localhost:8080'
);

/** 將 HTTP URL 轉為 WS URL */
function toWsUrl(httpUrl: string): string {
  if (!httpUrl || httpUrl === '') {
    // 同域名模式：用當前頁面的 host
    const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${proto}//${window.location.host}`;
  }
  return httpUrl.replace(/^http/, 'ws');
}

// --- Auth 相關類型 ---

export interface PlayerInfo {
  id: string;
  username: string;
  mmr: number;
  level: number;
  wins: number;
  losses: number;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
}

export interface AuthResponse {
  player: {
    id: string;
    username: string;
    mmr: number;
    level: number;
    xp: number;
    wins: number;
    losses: number;
    created_at: string;
    updated_at: string;
  };
  tokens: TokenPair;
}

// --- Matchmaking 相關類型 ---

export interface EnqueueResponse {
  status: string;
  estimated_wait: number;
  queue_size: number;
}

// --- WebSocket 訊息類型 ---

export type WSMessageType =
  | 'command'
  | 'event'
  | 'match_found'
  | 'match_timeout'
  | 'queue_status'
  | 'state_sync'
  | 'error'
  | 'ping'
  | 'pong';

export interface WSMessage {
  type: WSMessageType;
  payload?: unknown;
}

export interface MatchFoundPayload {
  game_id: string;
  opponent_name: string;
  side: 'player1' | 'player2';
}

export interface GameEventPayload {
  /** 伺服器 GameEvent.Type（JSON: "type"） */
  type: string;
  /** 伺服器 GameEvent.Data（JSON: "data"） */
  data?: Record<string, unknown>;
}

/** state_sync 訊息 payload */
export interface StateSyncPayload {
  game_id: string;
  state: unknown;
}

export interface WSErrorPayload {
  code: string;
  message: string;
}

export interface GameCommandPayload {
  game_id: string;
  command_type: string;
  data?: unknown;
}

// --- 回調類型 ---

export type WSMessageHandler = (msg: WSMessage) => void;

// ============================================================
// API 錯誤
// ============================================================

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ============================================================
// Token 管理
// ============================================================

const TOKEN_KEY = 'xlw_access_token';
const REFRESH_TOKEN_KEY = 'xlw_refresh_token';

function saveTokens(tokens: TokenPair): void {
  localStorage.setItem(TOKEN_KEY, tokens.access_token);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token);
}

function loadAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

function loadRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

// ============================================================
// REST API Client
// ============================================================

async function apiRequest<T>(
  method: string,
  path: string,
  body?: unknown,
  token?: string | null,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${SERVER_URL}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data: unknown = await res.json();

  if (!res.ok) {
    const errorData = data as { error?: string };
    throw new ApiError(res.status, errorData.error || '未知錯誤');
  }

  return data as T;
}

// --- 認證 API ---

/** 玩家註冊 */
export async function register(username: string, password: string): Promise<AuthResponse> {
  const resp = await apiRequest<AuthResponse>('POST', '/api/auth/register', { username, password });
  saveTokens(resp.tokens);
  return resp;
}

/** 玩家登入 */
export async function login(username: string, password: string): Promise<AuthResponse> {
  const resp = await apiRequest<AuthResponse>('POST', '/api/auth/login', { username, password });
  saveTokens(resp.tokens);
  return resp;
}

/** 刷新 Token */
export async function refreshToken(refToken: string): Promise<TokenPair> {
  const resp = await apiRequest<TokenPair>('POST', '/api/auth/refresh', { refresh_token: refToken });
  saveTokens(resp);
  return resp;
}

/** 取得當前玩家資訊 */
export async function getMe(token: string): Promise<PlayerInfo> {
  return apiRequest<PlayerInfo>('GET', '/api/auth/me', undefined, token);
}

// --- Deck 類型 ---

export interface DeckInfo {
  id: string;
  player_id: string;
  name: string;
  faction: string;
  card_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface DeckListResponse {
  decks: DeckInfo[];
}

export interface CreateDeckRequest {
  name: string;
  faction: string;
  card_ids: string[];
}

// --- Deck API ---

/** 取得玩家所有牌組 */
export async function listDecks(token: string): Promise<DeckInfo[]> {
  const resp = await apiRequest<DeckListResponse>('GET', '/api/decks', undefined, token);
  return resp.decks;
}

/** 取得單一牌組 */
export async function getDeck(token: string, deckId: string): Promise<DeckInfo> {
  return apiRequest<DeckInfo>('GET', `/api/decks/${deckId}`, undefined, token);
}

/** 建立牌組 */
export async function createDeck(token: string, req: CreateDeckRequest): Promise<DeckInfo> {
  return apiRequest<DeckInfo>('POST', '/api/decks', req, token);
}

/** 更新牌組 */
export async function updateDeck(token: string, deckId: string, req: Partial<CreateDeckRequest>): Promise<DeckInfo> {
  return apiRequest<DeckInfo>('PUT', `/api/decks/${deckId}`, req, token);
}

/** 刪除牌組 */
export async function deleteDeck(token: string, deckId: string): Promise<void> {
  await apiRequest<{ status: string }>('DELETE', `/api/decks/${deckId}`, undefined, token);
}

// --- 配對 API ---

/** 加入配對佇列 */
export async function enqueueMatch(token: string, deckId?: string): Promise<EnqueueResponse> {
  const body: Record<string, string> = {};
  if (deckId) {
    body.deck_id = deckId;
  }
  return apiRequest<EnqueueResponse>('POST', '/api/match/enqueue', body, token);
}

/** 取消配對 */
export async function cancelMatch(token: string): Promise<void> {
  await apiRequest<{ ok: boolean }>('DELETE', '/api/match/cancel', undefined, token);
}

/** 開始 AI 對戰 */
export async function startAIMatch(
  token: string,
  deckId: string,
  difficulty: 'balanced' | 'aggressive' | 'defensive' = 'balanced',
): Promise<{ game_id: string; status: string }> {
  return apiRequest<{ game_id: string; status: string }>('POST', '/api/match/ai', {
    deck_id: deckId,
    difficulty,
  }, token);
}

// ============================================================
// WebSocket Client
// ============================================================

interface ReconnectConfig {
  maxRetries: number;
  retryDelayMs: number;
}

const DEFAULT_RECONNECT: ReconnectConfig = {
  maxRetries: 5,
  retryDelayMs: 3000,
};

/** 心跳間隔（毫秒） */
const PING_INTERVAL_MS = 25_000;

/**
 * WebSocketClient 管理與伺服器的 WebSocket 連線
 */
export class WebSocketClient {
  private ws: WebSocket | null = null;
  private messageHandlers: Set<WSMessageHandler> = new Set();
  private reconnectConfig: ReconnectConfig;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private currentToken: string | null = null;
  private intentionalClose = false;

  constructor(reconnectConfig: ReconnectConfig = DEFAULT_RECONNECT) {
    this.reconnectConfig = reconnectConfig;
  }

  /** 建立 WebSocket 連線 */
  connect(token: string): void {
    this.currentToken = token;
    this.intentionalClose = false;
    this.reconnectAttempts = 0;
    this._connect(token);
  }

  /** 關閉 WebSocket 連線 */
  disconnect(): void {
    this.intentionalClose = true;
    this._cleanup();
  }

  /** 發送遊戲指令 */
  sendCommand(gameId: string, commandType: string, data?: unknown): void {
    const payload: GameCommandPayload = {
      game_id: gameId,
      command_type: commandType,
      data,
    };
    this._send({ type: 'command', payload });
  }

  /** 註冊訊息監聽器 */
  onMessage(handler: WSMessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  /** 檢查是否已連線 */
  get isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // --- 內部方法 ---

  private _connect(token: string): void {
    const wsUrl = `${toWsUrl(SERVER_URL)}/ws?token=${encodeURIComponent(token)}`;
    log.debug('正在連線...', wsUrl.replace(/token=.*/, 'token=***'));

    try {
      this.ws = new WebSocket(wsUrl);
    } catch (err) {
      log.error('WebSocket 建構失敗:', err);
      this._scheduleReconnect();
      return;
    }

    this.ws.onopen = () => {
      log.debug('連線成功');
      this.reconnectAttempts = 0;
      this._startPing();
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const msg = JSON.parse(event.data as string) as WSMessage;

        // 處理 Pong
        if (msg.type === 'pong') {
          return;
        }

        // 處理 Ping（伺服器發來的心跳）
        if (msg.type === 'ping') {
          this._send({ type: 'pong' });
          return;
        }

        // 分發給所有監聽器
        for (const handler of this.messageHandlers) {
          try {
            handler(msg);
          } catch (err) {
            log.error('訊息處理器錯誤:', err);
          }
        }
      } catch (err) {
        log.error('訊息解析錯誤:', err);
      }
    };

    this.ws.onclose = (event: CloseEvent) => {
      log.debug(`連線關閉: code=${String(event.code)} reason=${event.reason}`);
      this._stopPing();

      if (!this.intentionalClose) {
        this._scheduleReconnect();
      }
    };

    this.ws.onerror = (event: Event) => {
      log.error('連線錯誤:', event);
    };
  }

  private _send(msg: WSMessage): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      log.warn('無法發送，連線未就緒');
      return;
    }
    this.ws.send(JSON.stringify(msg));
  }

  private _startPing(): void {
    this._stopPing();
    this.pingTimer = setInterval(() => {
      this._send({ type: 'ping' });
    }, PING_INTERVAL_MS);
  }

  private _stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private _scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.reconnectConfig.maxRetries) {
      log.debug('已達最大重連次數，停止重連');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectConfig.retryDelayMs;
    log.debug(`${String(delay)}ms 後嘗試重連 (${String(this.reconnectAttempts)}/${String(this.reconnectConfig.maxRetries)})`,
    );

    this.reconnectTimer = setTimeout(() => {
      if (this.currentToken) {
        // 嘗試刷新 token 後重連
        const refToken = loadRefreshToken();
        if (refToken) {
          refreshToken(refToken)
            .then((newTokens) => {
              this.currentToken = newTokens.access_token;
              this._connect(newTokens.access_token);
            })
            .catch(() => {
              // 刷新失敗，用舊 token 重連
              if (this.currentToken) {
                this._connect(this.currentToken);
              }
            });
        } else {
          this._connect(this.currentToken);
        }
      }
    }, delay);
  }

  private _cleanup(): void {
    this._stopPing();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      if (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING) {
        this.ws.close();
      }
      this.ws = null;
    }
  }
}

// ============================================================
// 單例與工具
// ============================================================

/** 全域 WebSocket 客戶端實例 */
let wsClient: WebSocketClient | null = null;

/** 取得（或建立）全域 WebSocket 客戶端 */
export function getWSClient(): WebSocketClient {
  if (!wsClient) {
    wsClient = new WebSocketClient();
  }
  return wsClient;
}

// ============================================================
// Replay API
// ============================================================

// --- Replay 相關類型 ---

/** 後端 StoredEvent 結構 */
export interface StoredEvent {
  id: number;
  game_id: string;
  seq: number;
  event_type: string;
  player_id: string;
  turn_number: number;
  phase: string;
  event_data: unknown;
  created_at: string;
}

/** 後端 StoredCommand 結構 */
export interface StoredCommand {
  id: number;
  game_id: string;
  seq: number;
  player_id: string;
  command_type: string;
  command_data: unknown;
  turn_number: number;
  phase: string;
  created_at: string;
}

/** 後端 GameHistory 結構 */
export interface GameHistory {
  id: string;
  game_id: string;
  player1_id: string;
  player2_id: string;
  winner_id: string | null;
  win_condition: string;
  duration_secs: number;
  total_turns: number;
  p1_mmr_change: number;
  p2_mmr_change: number;
  created_at: string;
}

/** 對戰歷史列表回應 */
export interface GameHistoryListResponse {
  games: GameHistory[];
  total_count: number;
  offset: number;
  limit: number;
}

/** Replay 玩家資訊 */
export interface ReplayPlayers {
  player1_id: string;
  player2_id: string;
}

/** 完整重播資料 */
export interface ReplayData {
  version: string;
  game_id: string;
  players: ReplayPlayers;
  events: StoredEvent[];
  commands: StoredCommand[];
  result: GameHistory | null;
}

/** 快照回應 */
export interface SnapshotResponse {
  game_id: string;
  turn_number: number;
  exact: boolean;
  state: unknown;
}

// --- Replay API 函數 ---

/** 取得完整重播資料 */
export async function getReplay(token: string, gameId: string): Promise<ReplayData> {
  return apiRequest<ReplayData>('GET', `/api/replay/${gameId}`, undefined, token);
}

/** 取得指定回合的狀態快照 */
export async function getReplaySnapshot(token: string, gameId: string, turn: number): Promise<SnapshotResponse> {
  return apiRequest<SnapshotResponse>('GET', `/api/replay/${gameId}/snapshot/${String(turn)}`, undefined, token);
}

/** 取得對戰歷史列表 */
export async function getGameHistory(token: string, offset = 0, limit = 20): Promise<GameHistoryListResponse> {
  return apiRequest<GameHistoryListResponse>('GET', `/api/history?offset=${String(offset)}&limit=${String(limit)}`, undefined, token);
}

/** 匯出 Token 管理函數 */
export { saveTokens, loadAccessToken, loadRefreshToken, clearTokens };
